import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

const DEFAULT_YOUTUBE_SERVICE_NAME = 'YouTube Video';
const DEFAULT_YOUTUBE_SERVICE_ID = 'BJz7qNsdj-7';
const CONSENT_CHECK_INTERVAL_MS = 250;
const CONSENT_CHECK_MAX_ATTEMPTS = 80;

const normalize = (value) =>
  typeof value === 'string' ? value.trim().toLowerCase() : '';

const readConsentFromStorage = (serviceId, serviceName) => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return null;
  }

  try {
    const ucDataRaw = window.localStorage.getItem('ucData');
    if (ucDataRaw) {
      const ucData = JSON.parse(ucDataRaw);
      const consentServices = ucData?.consent?.services;
      const normalizedServiceName = normalize(serviceName);

      if (consentServices && typeof consentServices === 'object') {
        const byId = consentServices[serviceId];
        if (byId && typeof byId.consent === 'boolean') {
          return byId.consent;
        }

        const byName = Object.values(consentServices).find((entry) => {
          const entryName = normalize(entry && entry.name);
          return (
            entryName === normalizedServiceName ||
            entryName.includes('youtube')
          );
        });

        if (byName && typeof byName.consent === 'boolean') {
          return byName.consent;
        }
      }
    }

    const ucSettingsRaw = window.localStorage.getItem('uc_settings');
    if (ucSettingsRaw) {
      const ucSettings = JSON.parse(ucSettingsRaw);
      const services = Array.isArray(ucSettings?.services)
        ? ucSettings.services
        : [];
      const normalizedServiceName = normalize(serviceName);

      const byId = services.find((service) => service && service.id === serviceId);
      if (byId && typeof byId.status === 'boolean') {
        return byId.status;
      }

      const byName = services.find((service) => {
        const currentName = normalize(service && service.name);
        return (
          currentName === normalizedServiceName ||
          currentName.includes('youtube')
        );
      });

      if (byName && typeof byName.status === 'boolean') {
        return byName.status;
      }
    }
  } catch (error) {
    console.error('Usercentrics consent from storage could not be read:', error);
  }

  return null;
};

export default function Video(props) {
  const { video } = props;
  const embedUrl = `https://www.youtube.com/embed/${video.id}`;
  const serviceName =
    video.usercentricsService || DEFAULT_YOUTUBE_SERVICE_NAME;
  const serviceId = video.usercentricsId || DEFAULT_YOUTUBE_SERVICE_ID;
  const initialConsentStatus = readConsentFromStorage(serviceId, serviceName);
  const [isConsentGiven, setIsConsentGiven] = useState(
    initialConsentStatus === true
  );
  const [isConsentKnown, setIsConsentKnown] = useState(
    initialConsentStatus !== null
  );
  const [isSubmittingConsent, setIsSubmittingConsent] = useState(false);

  const getServicesBaseInfo = useCallback(() => {
    if (
      !window.UC_UI ||
      !window.UC_UI.isInitialized ||
      typeof window.UC_UI.getServicesBaseInfo !== 'function'
    ) {
      return [];
    }

    const services = window.UC_UI.getServicesBaseInfo();
    return Array.isArray(services) ? services : [];
  }, []);

  const resolveUcService = useCallback(() => {
    const services = getServicesBaseInfo();
    if (!services.length) {
      return null;
    }

    const byId = services.find((service) => service && service.id === serviceId);
    if (byId) {
      return byId;
    }

    const normalizedServiceName = normalize(serviceName);
    const byExactName = services.find(
      (service) => normalize(service && service.name) === normalizedServiceName
    );
    if (byExactName) {
      return byExactName;
    }

    const byYouTubeName = services.find((service) =>
      normalize(service && service.name).includes('youtube')
    );

    return byYouTubeName || null;
  }, [getServicesBaseInfo, serviceId, serviceName]);

  const getConsentStatus = useCallback(() => {
    try {
      const service = resolveUcService();

      if (service) {
        if (service.consent && typeof service.consent.status === 'boolean') {
          return service.consent.status;
        }
        if (typeof service.consent === 'boolean') {
          return service.consent;
        }
        if (typeof service.status === 'boolean') {
          return service.status;
        }
      }

      const ucDataRaw =
        window.localStorage && window.localStorage.getItem('ucData');

      if (ucDataRaw) {
        const ucData = JSON.parse(ucDataRaw);
        const consentServices = ucData?.consent?.services;

        if (consentServices && typeof consentServices === 'object') {
          const byId = consentServices[serviceId];
          if (byId && typeof byId.consent === 'boolean') {
            return byId.consent;
          }

          const normalizedServiceName = normalize(serviceName);
          const byName = Object.values(consentServices).find((entry) => {
            const entryName = normalize(entry && entry.name);
            return (
              entryName === normalizedServiceName ||
              entryName.includes('youtube')
            );
          });

          if (byName && typeof byName.consent === 'boolean') {
            return byName.consent;
          }
        }
      }
    } catch (error) {
      console.error('Usercentrics consent status could not be read:', error);
    }

    return null;
  }, [resolveUcService, serviceId, serviceName]);

  const syncConsentState = useCallback(() => {
    const consentStatus = getConsentStatus();
    if (typeof consentStatus !== 'boolean') {
      return false;
    }

    setIsConsentGiven(consentStatus);
    setIsConsentKnown(true);
    return true;
  }, [getConsentStatus]);

  useEffect(() => {
    let attempts = 0;
    let initInterval;

    if (!syncConsentState()) {
      initInterval = window.setInterval(() => {
        attempts += 1;

        if (syncConsentState() || attempts >= CONSENT_CHECK_MAX_ATTEMPTS) {
          window.clearInterval(initInterval);

          if (attempts >= CONSENT_CHECK_MAX_ATTEMPTS) {
            setIsConsentKnown(true);
          }
        }
      }, CONSENT_CHECK_INTERVAL_MS);
    }

    const onInitialized = () => {
      syncConsentState();
    };
    const onConsentChanged = () => {
      syncConsentState();
    };

    window.addEventListener('UC_UI_INITIALIZED', onInitialized);
    window.addEventListener('UC_UI_CMP_EVENT', onConsentChanged);

    return () => {
      window.removeEventListener('UC_UI_INITIALIZED', onInitialized);
      window.removeEventListener('UC_UI_CMP_EVENT', onConsentChanged);
      if (initInterval) {
        window.clearInterval(initInterval);
      }
    };
  }, [syncConsentState]);

  const getResolvedServiceId = useCallback(() => {
    const service = resolveUcService();
    return (service && service.id) || serviceId;
  }, [resolveUcService, serviceId]);

  const openConsentLayer = useCallback(() => {
    const resolvedServiceId = getResolvedServiceId();

    if (
      window.UC_UI &&
      typeof window.UC_UI.showSecondLayer === 'function' &&
      resolvedServiceId
    ) {
      window.UC_UI.showSecondLayer(resolvedServiceId);
      return;
    }

    if (window.UC_UI && typeof window.UC_UI.showFirstLayer === 'function') {
      window.UC_UI.showFirstLayer();
    }
  }, [getResolvedServiceId]);

  const allowVideo = async () => {
    setIsSubmittingConsent(true);

    try {
      const resolvedServiceId = getResolvedServiceId();

      if (
        window.UC_UI &&
        typeof window.UC_UI.acceptService === 'function' &&
        resolvedServiceId
      ) {
        await window.UC_UI.acceptService(resolvedServiceId);
      }

      if (
        window.__ucCmp &&
        typeof window.__ucCmp.saveConsents === 'function'
      ) {
        await window.__ucCmp.saveConsents();
      }

      setIsConsentGiven(true);
      setIsConsentKnown(true);

      window.setTimeout(() => {
        syncConsentState();
      }, 300);
    } catch (error) {
      console.error('Usercentrics consent update failed:', error);
      openConsentLayer();
    } finally {
      setIsSubmittingConsent(false);
    }
  };

  return (
    <VideoContainer>
      <IFrameWrapper data-uc-untouch>
        {isConsentGiven ? (
          <iframe
            title={video.title}
            src={embedUrl}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            loading="lazy"
            data-uc-untouch
          />
        ) : !isConsentKnown ? (
          <ConsentContainer data-uc-untouch>
            <ConsentLoading>Cookie-Einstellungen werden geladen...</ConsentLoading>
          </ConsentContainer>
        ) : (
          <ConsentContainer data-uc-untouch>
            <ConsentTitle>YouTube-Video freischalten</ConsentTitle>
            <ConsentText>
              Für die Anzeige des Videos ist Ihre Zustimmung zum Service
              &quot;{serviceName}&quot; erforderlich.
            </ConsentText>
            <ConsentPrimaryButton
              type="button"
              onClick={allowVideo}
              disabled={isSubmittingConsent}
            >
              Zustimmen und Video laden
            </ConsentPrimaryButton>
            <ConsentSecondaryButton type="button" onClick={openConsentLayer}>
              Datenschutz-Einstellungen
            </ConsentSecondaryButton>
            {!isConsentKnown && (
              <ConsentLoading>
                Cookie-Einstellungen werden geladen...
              </ConsentLoading>
            )}
          </ConsentContainer>
        )}
      </IFrameWrapper>

      {video.button && (
        <Button to={video.button.href}>{video.button.text}</Button>
      )}
    </VideoContainer>
  );
}

const VideoContainer = styled.section`
  padding: 50px 30px;
  text-align: center;
`;

const IFrameWrapper = styled.div`
  max-width: 800px;
  margin: 0 auto;
  overflow: hidden;
  aspect-ratio: 16 / 9;
  background: #f1f1f1;

  iframe {
    width: 100%;
    height: 100%;
    border: 0;
    display: block;
  }
`;

const ConsentContainer = styled.div`
  width: 100%;
  height: 100%;
  min-height: 320px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 24px;
  background: #ececec;
  border: 1px solid #dadada;
`;

const ConsentTitle = styled.h3`
  margin: 0;
  font-size: 24px;
  color: #303030;
  font-family: Montserrat, serif;
`;

const ConsentText = styled.p`
  margin: 0;
  max-width: 560px;
  color: #4d4d4d;
  font-size: 15px;
  line-height: 1.5;
`;

const ConsentPrimaryButton = styled.button`
  cursor: pointer;
  border: none;
  border-radius: 4px;
  padding: 12px 22px;
  background: #58a313;
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  font-family: Montserrat, serif;

  &:hover {
    background: #4a8c10;
  }

  &:disabled {
    opacity: 0.7;
    cursor: default;
  }
`;

const ConsentSecondaryButton = styled.button`
  cursor: pointer;
  border: none;
  border-radius: 4px;
  padding: 10px 18px;
  background: #f5f5f5;
  color: #303030;
  font-size: 14px;
  font-weight: 600;
  font-family: Open Sans, sans-serif;
`;

const ConsentLoading = styled.p`
  margin: 0;
  font-size: 13px;
  color: #666;
`;

const Button = styled(Link)`
  background-color: #58a313;
  color: #fff;
  margin-top: 30px;
  margin-bottom: 30px;
  font-size: 16px;
  padding: 20px 47px;
  font-weight: 700;
  text-transform: uppercase;
  text-decoration: none;
  white-space: nowrap;
  letter-spacing: 0.08em;
  font-family: Montserrat, serif;
  transition: 0.3s;
  display: inline-block;

  &:hover {
    background-color: #be996f;
  }

  @media (max-width: 480px) {
    font-size: 14px;
    padding: 20px 30px;
  }
`;

Video.propTypes = {
  video: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    usercentricsService: PropTypes.string,
    usercentricsId: PropTypes.string,
    button: PropTypes.shape({
      href: PropTypes.string,
      text: PropTypes.string,
    }),
  }).isRequired,
};
